import type { ReactNode } from "react";
import { Tag } from "@/components/tag";
import { Tooltip } from "@/components/tooltip";
import { Icon } from "@/components/icons";
import { Flag } from "@/components/flag";
import { ScoreChip } from "@/components/score-chip";
import {
  CollectionChipIcon,
  collapseKnownCollections,
  dataSourceLabel,
  selectionIdForApiToken,
} from "@/components/watchlists";
import { isoCountryName } from "@/app/(app)/_lib/iso-countries";
import { formatDate } from "@/app/(app)/_lib/format";
import type { EntityType } from "@/components/filter-cards";
import { entityTypeIcon } from "@/components/entity-type";

type SearchSummaryProps = {
  name: string;
  matchCount?: number;
  entityType?: EntityType;
  country?: string | string[];
  dob?: string | null;
  identifier?: string | null;
  minScore?: number;
  dataSource?: string | string[];
  monitoringSince?: string;
  actions?: ReactNode;
};

type SummaryTag = {
  key: string;
  chipIcon: ReactNode;
  tooltipIcon: ReactNode;
  tooltip: string;
  value: ReactNode;
};

export function SearchSummary({
  name,
  matchCount,
  entityType,
  country,
  dob,
  identifier,
  minScore,
  dataSource,
  monitoringSince,
  actions,
}: SearchSummaryProps) {
  const tags: SummaryTag[] = [];
  if (identifier) {
    tags.push({
      key: "identifier",
      chipIcon: <Icon name="barcode" size={20} />,
      tooltipIcon: <Icon name="barcode" size={24} className="shrink-0" />,
      tooltip: "External ID",
      value: identifier,
    });
  }
  if (entityType) {
    const iconName = entityTypeIcon(entityType);
    tags.push({
      key: "type",
      chipIcon: <Icon name={iconName} size={20} />,
      tooltipIcon: <Icon name={iconName} size={24} className="shrink-0" />,
      tooltip: "Entity type",
      value: entityType,
    });
  }
  const countries = (Array.isArray(country) ? country : country != null ? [country] : []).filter(
    Boolean,
  );
  for (const c of countries) {
    tags.push({
      key: `country-${c}`,
      chipIcon: <Flag iso={c} size={16} />,
      tooltipIcon: <Flag iso={c} size={24} />,
      tooltip: isoCountryName(c) || c,
      value: c,
    });
  }
  if (dob) {
    tags.push({
      key: "dob",
      chipIcon: <Icon name="calendar_today" size={20} />,
      tooltipIcon: <Icon name="calendar_today" size={24} className="shrink-0" />,
      tooltip: "Date of birth",
      value: formatDate(dob),
    });
  }
  if (typeof minScore === "number") {
    tags.push({
      key: "min-score",
      chipIcon: <Icon name="readiness_score" size={20} />,
      tooltipIcon: <Icon name="readiness_score" size={24} className="shrink-0" />,
      tooltip: "Minimum confidence score",
      value: null,
    });
  }
  const rawSources =
    dataSource == null ? [] : Array.isArray(dataSource) ? dataSource : [dataSource];
  const sources = collapseKnownCollections(rawSources);
  if (sources.length > 0) {
    const isSingle = sources.length === 1;
    const iconId = isSingle ? selectionIdForApiToken(sources[0]) : "coll:multiple";
    tags.push({
      key: "data-source",
      chipIcon: <CollectionChipIcon id={iconId} size={20} />,
      tooltipIcon: <CollectionChipIcon id={iconId} size={24} />,
      tooltip: isSingle ? dataSourceLabel(sources[0]) : sources.map(dataSourceLabel).join(", "),
      value: isSingle ? dataSourceLabel(sources[0]) : String(sources.length),
    });
  }
  if (monitoringSince) {
    tags.push({
      key: "monitoring-since",
      chipIcon: <Icon name="visibility" size={20} />,
      tooltipIcon: <Icon name="visibility" size={24} className="shrink-0" />,
      tooltip: "Monitoring since",
      value: formatDate(monitoringSince),
    });
  }

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-h4 text-black">
        {typeof matchCount === "number" ? `${name} (${matchCount})` : name}
      </h3>
      {tags.length > 0 ? (
        <div className="flex items-center justify-between gap-2">
          <ul aria-label="Search parameters" className="flex flex-wrap items-center gap-1.5">
            {tags.map((t) => (
              <li key={t.key}>
                <Tooltip icon={t.tooltipIcon} title={t.tooltip} align="start">
                  {t.key === "min-score" && typeof minScore === "number" ? (
                    <ScoreChip score={minScore} size="sm" />
                  ) : (
                    <Tag size="sm" tone="dark" leadingIcon={t.chipIcon}>
                      {t.value}
                    </Tag>
                  )}
                </Tooltip>
              </li>
            ))}
          </ul>
          {actions}
        </div>
      ) : (
        actions && <div className="flex justify-end">{actions}</div>
      )}
    </div>
  );
}
